<template>
  <div class="rounded-[20px] border border-gray-border bg-white shadow-panel-soft">
    <div class="flex flex-col gap-3 p-3 lg:hidden">
      <template v-if="loading">
        <div
          v-for="index in 3"
          :key="`orders-mobile-loading-${index}`"
          class="rounded-[18px] bg-white-gray p-4"
        >
          <div class="shimmer-row h-5 w-full rounded-[8px]"></div>
        </div>
      </template>

      <template v-else-if="tableOrders.length">
        <article
          v-for="(order, idx) in tableOrders"
          :key="order.guid || order.code"
          class="rounded-[18px] border border-gray-border bg-white-gray p-4"
        >
          <div class="flex items-start justify-between gap-3">
            <div>
              <p class="text-[12px] uppercase tracking-[0.06em] text-extra-text-color">
                Заказ {{ idx + 1 }}
              </p>
              <NuxtLink
                :to="orderLink(order)"
                class="mt-1 block text-[18px] font-helvetica-700 text-gray-dark underline decoration-1 underline-offset-2"
                @click="rememberOrderGuid(order)"
              >
                № {{ order.code }}
              </NuxtLink>
            </div>
            <p class="whitespace-nowrap text-[15px] text-extra-text-color">
              {{ order.date }}
            </p>
          </div>

          <div class="mt-4 grid gap-3 text-[15px] text-gray-dark">
            <div class="flex flex-col gap-1">
              <span class="text-[12px] uppercase tracking-[0.05em] text-extra-text-color">Статус</span>
              <span>{{ order.status || "—" }}</span>
            </div>
            <div class="flex flex-col gap-1">
              <span class="text-[12px] uppercase tracking-[0.05em] text-extra-text-color">Сумма</span>
              <span>{{ order.sum || "—" }}</span>
            </div>
            <div class="flex flex-col gap-1">
              <span class="text-[12px] uppercase tracking-[0.05em] text-extra-text-color">Организация</span>
              <span>{{ order.organization || "—" }}</span>
            </div>
            <div class="flex flex-col gap-1">
              <span class="text-[12px] uppercase tracking-[0.05em] text-extra-text-color">Контрагент</span>
              <span>{{ order.contractor || "—" }}</span>
            </div>
            <div class="flex flex-col gap-1">
              <span class="text-[12px] uppercase tracking-[0.05em] text-extra-text-color">Статус доставки</span>
              <span>{{ order.deliveryStatus || "—" }}</span>
            </div>
          </div>
        </article>
      </template>
    </div>

    <div class="hidden overflow-x-auto lg:block">
      <table class="min-w-[900px] w-full border-collapse text-left">
      <thead
        class="font-helvetica-700 font-bold text-[18px] text-gray-dark border-b border-gray-border"
      >
        <tr>
          <th class="w-[56px] py-4 pl-5 pr-3 font-bold"></th>
          <th class="py-4 px-3 font-bold">№ Заказа</th>
          <th class="py-4 px-3 font-bold">Дата заказа</th>
          <th class="py-4 px-3 font-bold">Статус</th>
          <th class="py-4 px-3 font-bold">Сумма (₽)</th>
          <th class="py-4 px-3 font-bold">Организация</th>
          <th class="py-4 px-3 font-bold">Контрагент</th>
          <th class="w-[180px] py-4 px-3 font-bold whitespace-nowrap">Статус доставки</th>
        </tr>
      </thead>
      <tbody
        class="divide-y divide-gray-border font-helvetica-400 font-normal text-[18px] text-gray-dark"
      >
        <tr v-if="loading">
          <td colspan="9" class="py-4 px-3">
            <div class="shimmer-row h-5 w-full rounded-[8px]"></div>
          </td>
        </tr>
        <tr
          v-else
          v-for="(order, idx) in tableOrders"
          :key="order.guid || order.code"
          class="hover:bg-white-gray transition-colors"
        >
          <td class="py-4 pl-5 pr-3 align-middle">{{ idx + 1 }}</td>
          <td class="py-4 px-3 align-middle">
            <NuxtLink
              :to="orderLink(order)"
              class="underline decoration-1 underline-offset-2 hover:text-orange"
              @click="rememberOrderGuid(order)"
            >
              {{ order.code }}
            </NuxtLink>
          </td>
          <td class="py-4 px-3 align-middle whitespace-nowrap">
            {{ order.date }}
          </td>
          <td class="py-4 px-3 align-middle">{{ order.status }}</td>
          <td class="py-4 px-3 align-middle whitespace-nowrap">
            {{ order.sum }}
          </td>
          <td class="py-4 px-3 align-middle">{{ order.organization }}</td>
          <td class="py-4 px-3 align-middle">{{ order.contractor }}</td>
          <td class="py-4 px-3 align-middle whitespace-nowrap">
            {{ order.deliveryStatus || "—" }}
          </td>
        </tr>
      </tbody>
      </table>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed } from "vue";
import { useReturnToLink } from "~/composables/useReturnToLink";

type Order = {
  code: string;
  date: string;
  status: string;
  sum: string;
  organization: string;
  contractor: string;
  deliveryStatus?: string;
  guid?: string;
};

const props = defineProps<{
  orders?: Order[];
  loading?: boolean;
}>();

const tableOrders = computed(() => props.orders ?? []);
const loading = computed(() => props.loading ?? false);
const { withReturnTo } = useReturnToLink();
const ORDER_GUID_CACHE_KEY = "apsh_order_guid_by_number";

const normalizeOrderCode = (value: string) => value.trim();

const readOrderGuidCache = () => {
  if (!process.client) return {};

  try {
    const raw = sessionStorage.getItem(ORDER_GUID_CACHE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch (error) {
    console.warn("Failed to read order guid cache", error);
    return {};
  }
};

const rememberOrderGuid = (order: Order) => {
  const code = normalizeOrderCode(order.code);
  const guid = order.guid?.trim();
  if (!process.client || !code || !guid) return;

  const cache = readOrderGuidCache();
  cache[code] = guid;
  sessionStorage.setItem(ORDER_GUID_CACHE_KEY, JSON.stringify(cache));
};

const orderLink = (order: Order) => {
  const id = order.guid?.trim() || normalizeOrderCode(order.code);
  return id ? withReturnTo(`/orders/${id}`) : undefined;
};
</script>

<style scoped>
@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

.shimmer-row {
  background: var(--gradient-shimmer);
  background-size: 200% 100%;
  animation: shimmer 1.4s infinite linear;
}
</style>
